/**
 * Ordered token substring search for list filters (e.g. template names).
 * Each whitespace-separated token must appear in order in the haystack (not necessarily adjacent).
 *
 * @example
 * matchesOrderedTokenSearch("CT FDM Thorax", "CT Thorax") // true
 */

/** Bound user query length (paste / abuse). */
const MAX_QUERY_LENGTH = 512;
/** Bound token count after split. */
const MAX_TOKENS = 32;
/** Bound field length scanned per row (pathological API data). */
const MAX_FIELD_SCAN_LENGTH = 8192;

/**
 * @param {unknown} value
 * @returns {string}
 */
function coerceFieldString(value) {
  if (value == null) return "";
  const s = String(value);
  const clipped =
    s.length > MAX_FIELD_SCAN_LENGTH ? s.slice(0, MAX_FIELD_SCAN_LENGTH) : s;
  try {
    return clipped.normalize("NFKC");
  } catch {
    return clipped;
  }
}

/**
 * @param {unknown} query
 * @returns {string[]}
 */
function getOrderedSearchTokens(query) {
  if (query == null) return [];
  let raw = String(query).trim();
  if (raw.length > MAX_QUERY_LENGTH) {
    raw = raw.slice(0, MAX_QUERY_LENGTH);
  }
  try {
    raw = raw.normalize("NFKC");
  } catch {
    // ignore
  }
  raw = raw.toLocaleLowerCase();
  if (!raw) return [];
  return raw.split(/\s+/).filter(Boolean).slice(0, MAX_TOKENS);
}

/**
 * @param {unknown} haystack Field value (e.g. template name).
 * @param {unknown} query User search string.
 * @returns {boolean} True if query is empty/whitespace-only, or all tokens match in order.
 */
export function matchesOrderedTokenSearch(haystack, query) {
  const tokens = getOrderedSearchTokens(query);
  if (tokens.length === 0) return true;

  const text = coerceFieldString(haystack).toLocaleLowerCase();
  let fromIndex = 0;
  for (const token of tokens) {
    const found = text.indexOf(token, fromIndex);
    if (found === -1) return false;
    fromIndex = found + token.length;
  }
  return true;
}

const NBSP = "\u00a0";

/** @param {unknown} subLabel */
export function anatomyTextFromSubLabel(subLabel) {
  const raw = coerceFieldString(subLabel).trim();
  if (!raw || raw === NBSP) return "";
  return raw.replace(/^anatomy:\s*/i, "").trim();
}

/**
 * Template picker filter — same as CRE PiP (`matchesTemplatePickerSearch`).
 * @param {{ label?: string, subLabel?: string, searchText?: string }} option
 * @param {unknown} query
 */
export function matchesTemplatePickerSearch(option, query) {
  if (getOrderedSearchTokens(query).length === 0) return true;

  const label = option?.label || "";
  const subLabel = option?.subLabel || "";
  const searchText = option?.searchText || "";
  const anatomyOnly = anatomyTextFromSubLabel(subLabel);

  return (
    matchesOrderedTokenSearch(label, query) ||
    matchesOrderedTokenSearch(subLabel, query) ||
    matchesOrderedTokenSearch(searchText, query) ||
    (anatomyOnly.length > 0 && matchesOrderedTokenSearch(anatomyOnly, query))
  );
}
